<template>
    <app-layout v-if="isPageReady">
        <div class="appVersion visHidden">
            {{ appVersion }}
        </div>
        <common-content-spacer height="medium-small">
            <workboard></workboard>

            <common-notification
                ref="commonSuccessNotificationChild"
                :content="$t('elements.home.invitations_sent.successful')"
            ></common-notification>

            <common-grid class="widget__container widget__min-height" justify="space-between">
                <common-grid-column class="widget__min-width" :width="7">
                    <index-home-rating :professionalRating="getActiveMemberRating" :rating="getRating"></index-home-rating>
                </common-grid-column>

                <common-grid-column v-if="!isTabletSmall" :width="8">
                    <index-members-nearby></index-members-nearby>
                </common-grid-column>

                <common-grid-column class="widget__min-width" :width="7">
                    <index-pending-connections></index-pending-connections>
                </common-grid-column>
            </common-grid>

            <referrals-analytic-overview v-if="isAllowedToSeeAnalyticReport && !isTablet"></referrals-analytic-overview>
        </common-content-spacer>
        <virtual-assistant v-if="isRedirectionBoxVisible" @close-redirection="toggleRedirectionBox"></virtual-assistant>
        <div v-if="!isTablet" class="cloud-container" :class="cloudContainerClass">
            <span class="cloud-text typewriter">{{ $t('elements.virtual_assistant.help') }}</span>
            <img src="/img/icons/cloud-sketched-shape.png" alt="" class="cloud-image" />
        </div>
        <div v-if="!isTablet" class="virtual-assistant-icon">
            <common-icon :name="chatBotName" size="gigantic" @click="toggleRedirectionBox"></common-icon>
        </div>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import Workboard from 'components/page-elements/index/workboard/Workboard'
    import ReferralsAnalyticOverview from 'components/page-elements/referrals-overview/ReferralsAnalyticOverview'
    import IndexPendingConnections from 'components/page-elements/index/IndexPendingConnections'
    import IndexMembersNearby from 'components/page-elements/index/IndexMembersNearby'
    import { version } from '../../../package.json'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import VirtualAssistant from 'pages/index/virtual-assistant/VirtualAssistant'
    import { Userpilot } from 'userpilot'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            IndexPendingConnections,
            Workboard,
            IndexMembersNearby,
            ReferralsAnalyticOverview,
            VirtualAssistant,
        },

        data() {
            return {
                userId: null,
                connections: [],
                PracticeTypeEnum: PracticeTypeEnum,
                isRedirectionBoxVisible: false,
                chatBotName: 'chat-bot-down',
                isCloudAnimating: false,
                cloudAnimationTimeoutId: null,
            }
        },

        computed: {
            isAllowedToSeeAnalyticReport() {
                return this.$auth.getActiveMember()?.isMasterUser
            },

            pageTitle() {
                return this.$t('pages.index.home.title')
            },

            appVersion() {
                return version
            },

            getRating() {
                return this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.$auth.getPractice()?.rating
                    : this.$auth.getActiveMember()?.rating
            },

            getActiveMemberRating() {
                return this.$auth.getActiveMember()?.professionalRating
            },

            cloudContainerClass() {
                return {
                    'is-animating': this.isCloudAnimating,
                }
            },
        },

        methods: {
            toggleRedirectionBox() {
                this.isRedirectionBoxVisible = !this.isRedirectionBoxVisible
                if (this.isRedirectionBoxVisible) {
                    this.chatBotName = 'chat-bot'
                } else {
                    this.chatBotName = 'chat-bot-down'
                }
                window.scrollTo(0, 0)
            },

            triggerUserpilot() {
                if (this.$auth.getUser()?.practiceType === 'demo-primary-care') {
                    setTimeout(() => {
                        Userpilot.trigger('c1RpVMmUxO')
                    }, 3000)
                }
                if (
                    this.$auth.getUser()?.practiceType === 'demo-physician' ||
                    this.$auth.getUser()?.practiceType === 'demo-ancillary'
                ) {
                    setTimeout(() => {
                        Userpilot.trigger('0MJhHfDrHg')
                    }, 3000)
                }
            },
        },

        mounted() {
            this.userId = this.$auth.getUser().id
            if (!localStorage.getItem('showRedirectionBox') && !this.isTablet && !this.$auth.isUserPracticeDemo()) {
                this.isRedirectionBoxVisible = true
            }
            if (this.$auth.isUserPracticeDemo()) {
                this.triggerUserpilot()
            }

            const animationMs = 18000
            this.isCloudAnimating = true
            this._cloudAnimationTimeoutId = setTimeout(() => {
                this.isCloudAnimating = false
                this._cloudAnimationTimeoutId = null
            }, animationMs)
        },
        beforeDestroy() {
            if (this._cloudAnimationTimeoutId) {
                clearTimeout(this._cloudAnimationTimeoutId)
                this._cloudAnimationTimeoutId = null
            }
        },
    }
</script>

<style lang="scss">
    .virtual-assistant-icon {
        position: absolute;
        bottom: -80px;
        right: 0;

        .icon {
            cursor: pointer;
            width: 90px;
            height: 90px;
            svg {
                display: block;
                width: auto;
                height: 100px;
                width: 100px;
            }
        }
    }
    .app-layout__content {
        padding-bottom: 160px;
    }
    @keyframes typing {
        0% {
            width: 0;
        }
        50% {
            width: 160px;
        }
        100% {
            width: 0;
        }
    }

    @keyframes float {
        0% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-10px);
        }
        100% {
            transform: translateY(0);
        }
    }

    /* default: no animation */
    .cloud-container {
        position: absolute;
        bottom: -40px;
        right: 80px;
        width: 100px;
        height: 100px;
        animation: none; /* <- ensure animation is not applied by default */
    }

    /* animation only when this class is present */
    .cloud-container.is-animating {
        animation: float 3s ease-in-out infinite;
    }

    /* typewriter runs only while animating */
    .cloud-container .typewriter {
        display: inline-block;
        overflow: hidden;
        white-space: nowrap;
        animation: none;
    }

    .cloud-container.is-animating .typewriter {
        animation: typing 5s steps(20, end) 1s infinite alternate;
    }

    .cloud-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .cloud-text {
        position: absolute;
        top: 50px;
        left: 50%;
        transform: translateX(-50%);
        font-weight: bold;
        font-size: 10px;
        color: #000;
        text-align: center;
        white-space: nowrap;
    }
</style>
